import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { ModalCardFlow } from '../../_designVariant';
import {
    HeaderContentCard,
    OutletModeCard,
    LayoutCard,
    NavigationMenuCard,
    BehaviourCard,
    NavbarFilterSortingCard,
} from '../cards';

/**
 * Body of the Navigation config modal — composes the four navigation-related
 * cards. Designed to be dropped inside any Dialog wrapper (the wrapper owns
 * its title/actions); this component is purely the content.
 *
 * value shape (each key optional, defaults handled by the cards):
 *   {
 *     headerContent:  { contact, brands, shopTheLook, outlet, giftVoucher, search, account, cart, ... }
 *     outletMode:     { minDiscount, hideFromExplore }
 *     layout:         { menuStyle, actionButtonsPosition, ... }
 *     navigationMenu: { categoriesShown, itemsPerRow, menuHeight, menuTextSize }
 *     behaviour:      { keepMenuVisibleWhileScrolling }
 *   }
 *
 * onChange(section, field, value) — fires per individual edit. The parent
 * decides where these end up (mini-api `setting` keys, in-memory only,
 * a future webshop-level config table — all TBD).
 */
const NavigationModal = ({
    value = {},
    onChange,
    shopFieldLabels,
    navbarOrder,
    onNavbarOrderChange,
    theme,
    t,
}) => {
    // Curry the parent onChange so each card sees its own (field, value) pair
    // without having to know which "section" key it lives under in the parent.
    const cardOnChange = (section) => (field, val) => {
        if (onChange) onChange(section, field, val);
    };

    return (
        // Wide modal → flow the cards into two balanced (masonry) columns on
        // large screens so the canvas is actually used; single column otherwise.
        <ModalCardFlow>
            <HeaderContentCard
                value={value.headerContent}
                onChange={cardOnChange('headerContent')}
                theme={theme}
                t={t}
            />
            <OutletModeCard
                value={value.outletMode}
                onChange={cardOnChange('outletMode')}
                theme={theme}
                t={t}
            />
            <LayoutCard
                value={value.layout}
                onChange={cardOnChange('layout')}
                theme={theme}
                t={t}
            />
            <NavigationMenuCard
                value={value.navigationMenu}
                onChange={cardOnChange('navigationMenu')}
                shopFieldLabels={shopFieldLabels}
                theme={theme}
                t={t}
            />
            <BehaviourCard
                value={value.behaviour}
                onChange={cardOnChange('behaviour')}
                theme={theme}
                t={t}
            />
            <NavbarFilterSortingCard
                orderRows={navbarOrder}
                onOrderRowsChange={onNavbarOrderChange}
                levelChosen={!!(value.navigationMenu?.categoriesShown && value.navigationMenu.categoriesShown[0])}
                theme={theme}
                t={t}
            />
        </ModalCardFlow>
    );
};

NavigationModal.propTypes = {
    value:              PropTypes.object,
    onChange:           PropTypes.func,
    shopFieldLabels:    PropTypes.object,
    navbarOrder:        PropTypes.array,
    onNavbarOrderChange: PropTypes.func,
    theme:              PropTypes.string,
    t:                  PropTypes.func,
};

export default NavigationModal;
